import { Fragment } from "react";
import type { CSSProperties, ReactNode } from "react";
import { resolveConfig, isPayoutMode, type PayoutInfoSummary, type StatementConfigData } from "../../api/statementConfig.ts";
import { Tag, Banner } from "./components.tsx";

/**
 * Safe (masked) payout-target view + fetch, shared by the statementInvoice
 * Approve confirm dialog and the read-only Invoice info modal. "Safe" = built
 * only from the resolve path's masked `payoutInfo` summary (server masks
 * IBAN/account); never the reveal path. No server change — one existing
 * resolveConfig("OPERATOR", …) call yields everything.
 */
export interface PayoutTarget {
  mode?: StatementConfigData["billingMode"];
  /** Masked summary — beneficiary/bank/BIC plaintext, account/IBAN masked. */
  payoutInfo: PayoutInfoSummary | null;
  odooRef?: string;
  /** res.partner database id from Admin. Every Odoo push sends it as partner_id
   *  verbatim, so a config without one cannot produce ANY invoice — worth saying
   *  before an approval, not after the push fails. */
  odooPartnerId?: number | null;
  odoo?: { journalCode?: string; payoutAccount?: string; feeAccount?: string };
  /** Any beneficiary/account/iban/giro present — drives the Payout warning. */
  hasPayoutTarget: boolean;
  /** Resolve failed / no operator — render a soft "unavailable" state. */
  unavailable?: boolean;
}

export async function fetchPayoutTarget(operatorID: number, asOf: string): Promise<PayoutTarget> {
  if (!operatorID || operatorID <= 0) {
    return { payoutInfo: null, hasPayoutTarget: false, unavailable: true };
  }
  const at = (asOf ?? "").slice(0, 10);
  try {
    const { data, payoutInfo } = await resolveConfig("OPERATOR", operatorID, at);
    const info = payoutInfo?.info ?? null;
    const odoo = data.odoo
      ? {
          journalCode: data.odoo.journalCode,
          payoutAccount: data.odoo.accountCodes?.payout,
          feeAccount: data.odoo.accountCodes?.fee,
        }
      : undefined;
    const hasPayoutTarget = !!(
      info &&
      (info.beneficiaryName || info.accountNumber || info.iban || info.bankgiro || info.postgiro)
    );
    return {
      mode: data.billingMode,
      payoutInfo: info,
      odooRef: data.partnerInfo?.odooRef,
      odooPartnerId: data.partnerInfo?.odooId ?? null,
      odoo,
      hasPayoutTarget,
    };
  } catch {
    return { payoutInfo: null, hasPayoutTarget: false, unavailable: true };
  }
}

const mono: CSSProperties = { fontFamily: "var(--font-family-mono, monospace)" };
const muted: CSSProperties = { color: "var(--color-text-muted)" };
const heading: CSSProperties = {
  fontSize: "var(--text-xs)",
  fontWeight: 600,
  color: "var(--color-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--tracking-wider)",
  marginBottom: "var(--space-1)",
};

/** Pure presentation — no fetch inside. `target` null + loading → spinner copy. */
export function PayoutTargetPanel({
  target,
  loading,
  payoutExpected,
}: {
  target: PayoutTarget | null;
  loading?: boolean;
  /** Host-supplied "this invoice is a payout, a target is required" signal.
   *  Drives the missing-target warning off the *invoice's* billing mode rather
   *  than the resolved config's — an operator with no config row still warns.
   *  Falls back to the resolved config mode when the host doesn't pass it. */
  payoutExpected?: boolean;
}) {
  const Head = <div style={heading}>Payout target</div>;

  if (loading) {
    return <div>{Head}<p style={{ margin: 0, ...muted }}>Loading payout target…</p></div>;
  }
  if (!target || target.unavailable) {
    return <div>{Head}<p style={{ margin: 0, ...muted }}>Payout target unavailable</p></div>;
  }

  const pi = target.payoutInfo;
  const showWarn = (payoutExpected ?? isPayoutMode(target.mode)) && !target.hasPayoutTarget;
  // Independent of billing mode and of the payout target: partner_id is on every
  // move SIP creates, in every mode, so without it nothing can be pushed at all.
  const hasPartnerId = !!target.odooPartnerId && target.odooPartnerId > 0;
  const rows: Array<[string, ReactNode]> = [];

  if (target.mode) {
    rows.push(["Mode", <Tag variant={isPayoutMode(target.mode) ? "info" : "solid"}>{target.mode}</Tag>]);
  }
  if (pi?.beneficiaryName) rows.push(["Beneficiary", pi.beneficiaryName]);
  if (pi?.accountNumber)   rows.push(["Account", <span style={mono}>{pi.accountNumber}</span>]);
  if (pi?.iban)            rows.push(["IBAN", <span style={mono}>{pi.iban}</span>]);
  if (pi?.bankgiro)        rows.push(["Bankgiro", <span style={mono}>{pi.bankgiro}</span>]);
  if (pi?.postgiro)        rows.push(["Postgiro", <span style={mono}>{pi.postgiro}</span>]);
  if (pi?.bankName)        rows.push(["Bank", pi.bankName]);
  if (pi?.bic)             rows.push(["BIC", <span style={mono}>{pi.bic}</span>]);
  if (target.odooRef)      rows.push(["Odoo ref", <span style={mono}>{target.odooRef}</span>]);
  rows.push([
    "Odoo partner id",
    hasPartnerId
      ? <span style={mono}>{target.odooPartnerId}</span>
      : <span style={{ color: "var(--color-danger, #c00)" }}>not set</span>,
  ]);
  if (target.odoo?.journalCode) {
    const acct = [target.odoo.payoutAccount, target.odoo.feeAccount].filter(Boolean).join(" / ");
    rows.push([
      "Odoo journal",
      <span><span style={mono}>{target.odoo.journalCode}</span>{acct ? <span style={muted}> · {acct}</span> : null}</span>,
    ]);
  }

  return (
    <div>
      {Head}
      {showWarn && (
        <div style={{ marginBottom: "var(--space-2)" }}>
          <Banner variant="error">No payout target configured for this operator.</Banner>
        </div>
      )}
      {!hasPartnerId && (
        <div style={{ marginBottom: "var(--space-2)" }}>
          <Banner variant="error">
            No Odoo partner id (partnerInfo.odooId) for this operator — the Odoo push will fail for
            every invoice until it is set in the statement config.
          </Banner>
        </div>
      )}
      {rows.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            rowGap: "var(--space-1)",
            columnGap: "var(--space-3)",
            fontSize: "var(--text-sm)",
          }}
        >
          {rows.map(([k, v], i) => (
            <Fragment key={i}>
              <span style={muted}>{k}</span>
              <span>{v}</span>
            </Fragment>
          ))}
        </div>
      ) : (
        !showWarn && <p style={{ margin: 0, ...muted }}>No payout target details.</p>
      )}
    </div>
  );
}
